import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import InfoTip from '@/components/InfoTip';
import { aud } from '@/utils/format';
import {
  targetBoard,
  targetWeek,
  depositSessionsCount,
  studioDepositSessionsTarget,
  artistLinkDeposits,
  socialConversionStrikeRate,
  reviewsDelta,
  conversionStatusBlock,
  newClientsByArtistWeek,
  type TargetCell,
} from '@/domain/reportingEngine';
import { leadThresholdBand, LEAD_BAND_LABEL, type LeadThresholdBand } from '@/data/reportingTargets';
import type { StudioId } from '@/domain/types';

const BAND_CLASS: Record<LeadThresholdBand, string> = {
  meet: 'text-cream-soft',
  acceptable: 'text-gold-bright',
  'very-good': 'text-mint',
};

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
  });
}

function fullDate(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function cellClass(cell: TargetCell): string {
  if (!cell.elapsed) return 'text-cream-faint/40';
  if (cell.sessions === 0) return 'bg-blood/10 text-cream-faint';
  return cell.onPace ? 'bg-mint/20 text-cream' : 'bg-blood/20 text-cream';
}

function paceClass(ok: boolean): string {
  return ok ? 'text-mint' : 'text-blood';
}

/** The Targets board (Prompt F, Stage 3): per studio, artists x days, red/green
 * vs seeded targets, average job value, the Thu-Tue pace tally and un-worked leads. */
export default function TargetsTab({ studioId: shellStudioId }: { studioId?: StudioId }) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const artists = useBrainStore((s) => s.artists);
  const customers = useBrainStore((s) => s.customers);
  const deposits = useBrainStore((s) => s.deposits);
  const sequenceState = useBrainStore((s) => s.sequenceState);

  const activeStudios = useMemo(
    () => Object.values(studios).filter((s) => !s.archived),
    [studios],
  );
  // Targets is inherently per-studio; when the shell filter is "All studios" we
  // show the first studio and hint how to switch.
  const studioId = (shellStudioId ?? activeStudios[0]?.id) as StudioId | undefined;
  const defaultedFromAll = !shellStudioId && Boolean(studioId);

  const board = useMemo(
    () => (studioId ? targetBoard(studioId, clockIso) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, clockIso, bookings, salePayments, artists, customers],
  );

  // G5 ops-sheet parity, all scoped to the same target week + selected studio.
  const weekDays = useMemo(() => targetWeek(clockIso).days, [clockIso]);
  const depositSessions = useMemo(
    () => (studioId ? depositSessionsCount(weekDays, studioId) : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDays, studioId, bookings, deposits],
  );
  const depositSessionsTarget = useMemo(
    () => (studioId ? studioDepositSessionsTarget(studioId) : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, artists],
  );
  const reviews = useMemo(
    () => (studioId ? reviewsDelta(weekDays, studioId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDays, studioId, studios],
  );
  const artistLink = useMemo(
    () => (studioId ? artistLinkDeposits(weekDays, studioId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDays, studioId, deposits, customers],
  );
  const social = useMemo(
    () => (studioId ? socialConversionStrikeRate(weekDays, studioId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDays, studioId, customers, deposits],
  );
  const statusBlock = useMemo(
    () => (studioId ? conversionStatusBlock(studioId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, customers, sequenceState],
  );
  const newClients = useMemo(
    () => (studioId ? newClientsByArtistWeek(weekDays, studioId) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDays, studioId, bookings],
  );

  if (!board) {
    return (
      <div className="panel p-8 max-w-3xl">
        <p className="font-serif text-cream-soft">No studios to show.</p>
      </div>
    );
  }

  const lastDay = board.days[board.days.length - 1]!;
  const oldestAgeDays = board.unworkedLeads.oldestIso
    ? Math.floor(
        (new Date(clockIso).getTime() - new Date(board.unworkedLeads.oldestIso).getTime()) /
          (24 * 60 * 60_000),
      )
    : null;

  return (
    <div className="space-y-4 max-w-6xl" data-testid="reporting-targets">
      {/* Studio label (driven by the shell studio filter) + target-week framing */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div data-testid="targets-studio-label">
          <p className="label-small-caps text-gold-bright">Studio</p>
          <p className="font-serif text-lg text-cream mt-1">{studios[studioId!]?.name ?? studioId}</p>
          {defaultedFromAll ? (
            <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-0.5">
              Pick a studio in the filter above to switch
            </p>
          ) : null}
        </div>
        <p className="font-inter text-[11px] text-cream-soft uppercase tracking-widest">
          Target week {fullDate(board.thursday)} - {fullDate(lastDay)} · reviewed{' '}
          {fullDate(board.reviewDayIso)} · day {board.daysElapsed} of {board.daysTotal}
        </p>
      </div>

      {/* Pace tally + un-worked leads */}
      <div className="grid grid-cols-3 gap-3">
        <div className="panel-dark p-4" data-testid="targets-pace-sessions">
          <p className="label-small-caps flex items-center gap-1">
            Sessions this week
            <InfoTip>
              Completed sessions so far this target week (Thu-Tue, reviewed Wed) vs the studio&apos;s weekly session
              target. &quot;Should be at&quot; pro-rates the target by how many of the six days have elapsed.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', paceClass(board.totals.sessions >= board.totals.expectedSessionsByNow))}>
            {board.totals.sessions}
            <span className="text-cream-faint text-lg"> / {board.totals.targetSessions}</span>
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            Should be at {Math.round(board.totals.expectedSessionsByNow)} by now
          </p>
        </div>
        <div className="panel-dark p-4" data-testid="targets-pace-dollars">
          <p className="label-small-caps flex items-center gap-1">
            Dollars this week
            <InfoTip>
              Completed-session dollars so far this target week vs the weekly dollar target, with the same pro-rata
              &quot;should be at&quot; pace. This is the figure that hid the $20k shortfall when only sessions were tracked.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', paceClass(board.totals.dollarsAud >= board.totals.expectedDollarsByNow))}>
            {aud(board.totals.dollarsAud)}
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            Target {aud(board.totals.targetDollarsAud)} · should be at{' '}
            {aud(Math.round(board.totals.expectedDollarsByNow))}
          </p>
        </div>
        <div
          className={clsx('panel-dark p-4', board.unworkedLeads.count > 0 && 'border border-blood/40')}
          data-testid="targets-unworked-leads"
        >
          <p className="label-small-caps flex items-center gap-1">
            Un-worked leads
            <InfoTip>
              Active leads (new or qualifying) this studio has never reached out to — no outbound touch. The early
              warning behind the Collingwood 20% miss: leads &quot;literally sitting there&quot;. Shows the oldest one&apos;s age.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', board.unworkedLeads.count > 0 ? 'text-blood' : 'text-mint')}>
            {board.unworkedLeads.count}
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            {oldestAgeDays === null
              ? 'All leads worked'
              : `Oldest sitting ${oldestAgeDays} day${oldestAgeDays === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>

      {/* G5: ops-sheet parity — deposit sessions + reviews */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="panel-dark p-4" data-testid="targets-deposit-sessions">
          <p className="label-small-caps flex items-center gap-1">
            Deposit sessions
            <InfoTip>
              How many booked sessions this week have a deposit behind them - not how many payments came in. One
              deposit that holds four sessions of a big piece counts as four. This is the number Zack means by
              &quot;deposits&quot; on the ops sheet: booked, deposit-backed work.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', depositSessionsTarget > 0 && depositSessions >= depositSessionsTarget ? 'text-mint' : depositSessionsTarget > 0 ? 'text-blood' : 'text-gold-bright')}>
            {depositSessions}
            {depositSessionsTarget > 0 ? <span className="text-cream-faint text-lg"> / {depositSessionsTarget}</span> : null}
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            Booked sessions secured by a deposit{depositSessionsTarget > 0 ? ' vs target' : ''}
          </p>
        </div>
        <div className="panel-dark p-4" data-testid="targets-reviews">
          <p className="label-small-caps flex items-center gap-1">
            Reviews this week
            <InfoTip>
              New customer reviews this target week versus the studio&apos;s weekly review target, with the change from
              last week. Green means the target is met. (Seeded demo data - swap in the real review feed when it is
              connected.)
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', reviews && reviews.meetsTarget ? 'text-mint' : 'text-blood')}>
            {reviews?.thisWeek ?? 0}
            <span className="text-cream-faint text-lg"> / {reviews?.target ?? 0}</span>
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            {reviews
              ? `${reviews.delta >= 0 ? '+' : ''}${reviews.delta} vs last week`
              : 'No review data'}
          </p>
        </div>
      </div>

      {/* G5: lead status block (item 17a) */}
      <section className="panel p-4" data-testid="targets-status-block">
        <h2 className="heading-serif text-base text-cream mb-1 flex items-center gap-1">
          Lead status
          <InfoTip>
            Where this studio&apos;s leads currently sit, mirroring the status colours on Zack&apos;s sheet. Active
            (green) are being worked; booked and sorted have converted; unresponsive (light red) never answered;
            closed (dark red) said no. Built from each lead&apos;s stage and the call-sequence outcome - no new data
            entry.
          </InfoTip>
        </h2>
        <div className="flex flex-wrap gap-2 mt-2">
          {statusBlock.map((s) => (
            <span
              key={s.key}
              data-testid={`targets-status-${s.key}`}
              className="rounded-full border border-gold-dim/25 bg-ink-soft/40 px-3 py-1 text-xs font-inter text-cream-soft"
            >
              {s.label} · <span className="text-gold-bright font-mono">{s.count}</span>
            </span>
          ))}
        </div>
      </section>

      {/* G5 (item 15): social-conversion strike rate — its own card, kept separate
          from the artist-link booked totals so each figure reads on its own. */}
      {social ? (
        <section className="panel p-4" data-testid="targets-social-strike">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="heading-serif text-base text-cream flex items-center gap-1">
              Social conversion
              <InfoTip>
                Of this week&apos;s social-DM leads (Instagram/Facebook), the share that paid a deposit - with
                artist-link deposits taken out, so social outreach only gets credit for social conversions. The
                excluded artist-link count is shown beneath the rate.
              </InfoTip>
            </h2>
            <div className="text-right">
              <p className="font-cinzel text-3xl text-gold-bright leading-none">
                {social.strikeRatePct === null ? '—' : `${Math.round(social.strikeRatePct)}%`}
              </p>
              <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-1">
                {social.conversions}/{social.socialLeads} converted · {social.excludedArtistLink} artist-link excluded
              </p>
            </div>
          </div>
          {social.socialLeads === 0 ? (
            <p className="font-serif text-cream-soft text-sm mt-2">
              No social-DM leads with a first touch this week yet.
            </p>
          ) : null}
        </section>
      ) : null}

      {/* G5: artist-link booked totals (item 15) */}
      <section className="panel p-4" data-testid="targets-artist-link">
        <h2 className="heading-serif text-base text-cream flex items-center gap-1 mb-2">
          Artist-link booked totals
          <InfoTip>
            Deposits taken this week through an artist&apos;s own booking link, per artist, with the client names.
            These come from the artist&apos;s link, not social outreach, so they are shown here and are NOT counted
            in the social conversion rate above.
          </InfoTip>
        </h2>
        {artistLink && artistLink.byArtist.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left px-2 py-2">Artist</th>
                  <th className="text-right px-2 py-2">Deposits</th>
                  <th className="text-right px-2 py-2">Value</th>
                  <th className="text-left px-2 py-2">Clients</th>
                </tr>
              </thead>
              <tbody>
                {artistLink.byArtist.map((r) => (
                  <tr key={r.artistId} className="border-t border-gold-dim/15" data-testid={`targets-artist-link-${r.artistId}`}>
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">
                      {artists[r.artistId]?.displayName ?? r.artistId}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-gold-bright">{r.count}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">{aud(r.amountAud)}</td>
                    <td className="px-2 py-2 font-inter text-cream-soft">{r.clientNames.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="font-serif text-cream-soft text-sm">No artist-link deposits this week.</p>
        )}
      </section>

      {/* Artist x day board */}
      <section className="panel p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Artist</th>
                {board.days.map((d) => (
                  <th key={d} className="text-center px-1 py-2 whitespace-nowrap">
                    {dayLabel(d)}
                  </th>
                ))}
                <th className="text-right px-2 py-2">
                  <span className="inline-flex items-center gap-1">
                    Avg job
                    <InfoTip align="right">
                      Average dollars per completed session this week (dollars ÷ sessions). A low average with
                      on-target session counts is the $20k-shortfall signal — busy but cheap work.
                    </InfoTip>
                  </span>
                </th>
                <th className="text-right px-2 py-2">
                  <span className="inline-flex items-center gap-1">
                    New clients
                    <InfoTip align="right">
                      How many brand-new clients this artist booked this week (their first-ever session lands this
                      week). The band flags it: Meeting (0-2), Good (3-5), Very good (6+). Client sets the real bands.
                    </InfoTip>
                  </span>
                </th>
                <th className="text-right px-2 py-2">Sessions</th>
                <th className="text-right px-2 py-2">Dollars</th>
              </tr>
            </thead>
            <tbody>
              {board.rows.map((row) => (
                <tr key={row.artistId} className="border-t border-gold-dim/15">
                  <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{row.name}</td>
                  {row.cells.map((cell) => (
                    <td key={cell.dayIso} className="px-1 py-1 text-center">
                      <div
                        className={clsx('rounded-sm py-1 px-0.5', cellClass(cell))}
                        title={`${row.name} · ${dayLabel(cell.dayIso)}: ${cell.sessions} sessions · ${aud(cell.dollarsAud)}`}
                      >
                        <div className="font-mono text-sm leading-none">
                          {cell.elapsed || cell.sessions > 0 ? cell.sessions : '·'}
                        </div>
                        <div className="font-mono text-[9px] text-cream-soft leading-none mt-0.5">
                          {cell.dollarsAud > 0 ? aud(cell.dollarsAud) : ''}
                        </div>
                      </div>
                    </td>
                  ))}
                  <td
                    className={clsx(
                      'px-2 py-2 text-right font-mono',
                      row.avgJobValueAud > 0 ? 'text-gold-bright' : 'text-cream-faint',
                    )}
                    data-testid={`targets-avgjob-${row.artistId}`}
                  >
                    {row.avgJobValueAud > 0 ? aud(row.avgJobValueAud) : '—'}
                  </td>
                  <td
                    className={clsx('px-2 py-2 text-right font-mono', BAND_CLASS[leadThresholdBand(newClients[row.artistId] ?? 0)])}
                    data-testid={`targets-newclients-${row.artistId}`}
                  >
                    {newClients[row.artistId] ?? 0}
                    <span className="text-cream-faint text-[9px] uppercase tracking-widest ml-1">
                      {LEAD_BAND_LABEL[leadThresholdBand(newClients[row.artistId] ?? 0)]}
                    </span>
                  </td>
                  <td className={clsx('px-2 py-2 text-right font-mono', paceClass(row.sessionsOnPace))}>
                    {row.sessions}
                    <span className="text-cream-faint"> / {row.targetSessions}</span>
                  </td>
                  <td className={clsx('px-2 py-2 text-right font-mono', paceClass(row.dollarsOnPace))}>
                    {aud(row.dollarsAud)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gold-dim/30 text-cream">
                <td className="px-2 py-2 label-small-caps text-gold-bright">All artists</td>
                <td colSpan={board.days.length} />
                <td className="px-2 py-2" />
                <td className="px-2 py-2" />
                <td className={clsx('px-2 py-2 text-right font-mono', paceClass(board.totals.sessions >= board.totals.expectedSessionsByNow))}>
                  {board.totals.sessions} / {board.totals.targetSessions}
                </td>
                <td className={clsx('px-2 py-2 text-right font-mono', paceClass(board.totals.dollarsAud >= board.totals.expectedDollarsByNow))}>
                  {aud(board.totals.dollarsAud)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-3">
          Green = on pace vs target · red = behind · avg job value is the $20k-shortfall catcher
        </p>
      </section>
    </div>
  );
}
